import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import ImageUpload from '../../components/ImageUpload';
import { Plus, Edit2, Trash2, X, Save, Bell, AlertTriangle, AlertCircle, Info, Filter } from 'lucide-react';
import axios from 'axios';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { toast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAnnouncements = () => {
  const { getAuthHeader } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedPriority, setSelectedPriority] = useState('All');
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    date: new Date().toISOString().split('T')[0],
    priority: 'normal',
    image: ''
  });

  const priorities = [
    { value: 'urgent', label: 'Urgent', color: 'red', icon: AlertTriangle },
    { value: 'important', label: 'Important', color: 'yellow', icon: AlertCircle },
    { value: 'normal', label: 'Normal', color: 'blue', icon: Info }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/announcements`);
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/announcements/${editingId}`, formData, { headers: getAuthHeader() });
        toast({ title: 'Success', description: 'Announcement updated successfully' });
      } else {
        await axios.post(`${API_URL}/api/announcements`, formData, { headers: getAuthHeader() });
        toast({ title: 'Success', description: 'Announcement created successfully' });
      }
      resetForm();
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save announcement', variant: 'destructive' });
    }
  };

  const handleEdit = (item) => {
    setFormData({
      title: item.title,
      content: item.content,
      date: item.date,
      priority: item.priority || 'normal',
      image: item.image || ''
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;
    try {
      await axios.delete(`${API_URL}/api/announcements/${id}`, { headers: getAuthHeader() });
      toast({ title: 'Success', description: 'Announcement deleted' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete announcement', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      date: new Date().toISOString().split('T')[0],
      priority: 'normal',
      image: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const getPriorityConfig = (priority) => {
    return priorities.find(p => p.value === priority) || priorities[2];
  };

  const filteredItems = selectedPriority === 'All'
    ? items
    : items.filter(item => (item.priority || 'normal') === selectedPriority);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Announcements</h1>
            <p className="text-gray-500 mt-1">Manage district announcements and notices</p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg flex items-center space-x-2 transition-colors shadow-lg"
          >
            <Plus className="w-5 h-5" />
            <span>Add Announcement</span>
          </button>
        </div>

        {/* Priority Filter */}
        <div className="bg-white rounded-xl shadow-lg p-4">
          <div className="flex items-center space-x-4">
            <Filter className="w-5 h-5 text-gray-500" />
            <span className="font-medium text-gray-700">Filter by Priority:</span>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedPriority('All')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedPriority === 'All'
                    ? 'bg-yellow-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All ({items.length})
              </button>
              {priorities.map(p => {
                const count = items.filter(i => (i.priority || 'normal') === p.value).length;
                return (
                  <button
                    key={p.value}
                    onClick={() => setSelectedPriority(p.value)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center space-x-1 ${
                      selectedPriority === p.value
                        ? `bg-${p.color}-500 text-white`
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    style={selectedPriority === p.value ? { backgroundColor: p.color === 'red' ? '#ef4444' : p.color === 'yellow' ? '#eab308' : '#3b82f6' } : {}}
                  >
                    <p.icon className="w-4 h-4" />
                    <span>{p.label} ({count})</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingId ? 'Edit Announcement' : 'Add New Announcement'}
              </h2>
              <button onClick={resetForm} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-6 h-6 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Enter announcement title"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  >
                    {priorities.map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Enter announcement content..."
                    rows="5"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  />
                </div>

                <div className="md:col-span-2">
                  <ImageUpload
                    value={formData.image}
                    onChange={(url) => setFormData({ ...formData, image: url })}
                    label="Announcement Image (Optional)"
                  />
                </div>
              </div>

              <div className="flex space-x-4 pt-4 border-t border-gray-200">
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg flex items-center justify-center space-x-2 transition-colors"
                >
                  <Save className="w-5 h-5" />
                  <span>{editingId ? 'Update Announcement' : 'Create Announcement'}</span>
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Announcements List */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading announcements...</div>
          ) : filteredItems.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {selectedPriority === 'All' 
                ? 'No announcements yet. Create your first one!'
                : `No ${selectedPriority} priority announcements`}
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredItems.map((item) => {
                const priorityConfig = getPriorityConfig(item.priority);
                const PriorityIcon = priorityConfig.icon;
                return (
                  <div key={item.id} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-bold text-gray-900">{item.title}</h3>
                          <span 
                            className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center space-x-1`}
                            style={{ 
                              backgroundColor: priorityConfig.color === 'red' ? '#fef2f2' : priorityConfig.color === 'yellow' ? '#fefce8' : '#eff6ff',
                              color: priorityConfig.color === 'red' ? '#dc2626' : priorityConfig.color === 'yellow' ? '#ca8a04' : '#2563eb'
                            }}
                          >
                            <PriorityIcon className="w-3 h-3" />
                            <span>{priorityConfig.label}</span>
                          </span>
                          <span className="px-3 py-1 bg-gray-100 text-gray-700 text-xs font-semibold rounded-full">
                            {item.date}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-3 line-clamp-2">{item.content}</p>
                        {item.image && (
                          <img src={item.image} alt={item.title} className="w-32 h-20 object-cover rounded-lg" />
                        )}
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => handleEdit(item)}
                          className="p-2 bg-yellow-100 hover:bg-yellow-200 text-yellow-700 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminAnnouncements;
